import React, {useMemo} from 'react';
import {hasFeatureFlag} from '../../util/FeatureUtil';
import {deriveEligibilityFromTask} from './eligibility';
import {NovaInvocationControl} from './NovaInvocationControl';
import {useNovaInvocationState} from './useNovaInvocationState';

export interface ConnectedNovaInvocationControlProps {
	taskId: string;
	// Threaded through to invoke() for the notification a completed run may raise — this component
	// has no other way to know the task's display name.
	taskName?: string;
	// Same reasoning as taskName — lets the notification link to the task from anywhere, not just
	// the page the run started on.
	companyProjectId?: string | number;
	companyTaskId?: string | number;
	// For the notification's project-code pill — same reasoning as companyProjectId/companyTaskId.
	projectColor?: string;
	// See InvocationControlProps.instanceKey (NovaInvocationControl.tsx) — must match whatever
	// key the agentPicker prop below was itself given.
	instanceKey?: string;
	readOnly: boolean;
	// From Util.isClientTaskActionsRestricted(viewer) — the caller already computes this today
	// for its own disabled/locked logic; this surfaces it as a named "role" reason instead of a
	// blunt disable. Optional so callers with no client-role concern (or that haven't been
	// updated yet) can omit it rather than being forced to pass a hardcoded false.
	isClientActionsRestricted?: boolean;
	onOpenTask: () => void;
	agentPicker?: React.ReactNode;
}

export const ConnectedNovaInvocationControl = (props: ConnectedNovaInvocationControlProps): JSX.Element | null => {
	const isEnabled = hasFeatureFlag('nova_agent_invocation');
	// TaskRow.js's own memo() shallow-compares draggedNode, which changes for every visible row
	// once any row starts dragging — this component's render body runs on that same cadence
	// regardless, but memoizing here at least stops re-deriving eligibility (and re-allocating the
	// input object) on every one of those re-renders when neither underlying input actually changed.
	const eligibility = useMemo(
		() =>
			deriveEligibilityFromTask({
				readOnly: {isReadOnly: props.readOnly},
				isClientActionsRestricted: !!props.isClientActionsRestricted,
			}),
		[props.readOnly, props.isClientActionsRestricted]
	);
	// Per product: unlike task_locked/run_cap_reached (both temporary, cleared by something the
	// person can see happen), role is a permission this person will never grant themselves — the
	// blocked reason is disclosed elsewhere for a role-restricted account instead, so this control
	// renders nothing at all instead of a disabled pill, the same as the flag being off. task_locked
	// still wins when both apply (eligibility's own priority order), and stays a visible disabled
	// state.
	const isHiddenForRole = eligibility === 'role';

	// Hooks run unconditionally on every render — Rules of Hooks forbids skipping the call itself
	// — but passing enabled through lets the hook skip its own store subscriptions when the control
	// won't render anyway (flag off, or hidden for role), so that account isn't paying for work
	// behind a control that renders null anyway.
	// accountLimitNotice isn't rendered here — the row is not its host (see NovaInvocationControl.tsx's
	// own comment); the hook still computes it for whenever a real host, like a task-detail composer,
	// exists to show it.
	const {state, onAction, onOpenOutput, onStop} = useNovaInvocationState(
		props.taskId,
		eligibility,
		isEnabled && !isHiddenForRole,
		props.taskName,
		props.companyProjectId,
		props.companyTaskId,
		props.projectColor
	);

	if (!isEnabled || isHiddenForRole) return null;

	return (
		<NovaInvocationControl
			taskId={props.taskId}
			instanceKey={props.instanceKey}
			state={state}
			onAction={onAction}
			onOpenOutput={() => {
				onOpenOutput();
				// markRead() above dispatches NOVA_INVOCATION_STATE_CHANGED synchronously to every
				// subscribed control on the page (event_manager.ts has no per-listener error/queue
				// isolation), and onOpenTask() is a heavy operation of its own — a route change plus
				// the real task modal's data fetch. Running both in the same synchronous burst, on a
				// page that may host several other subscribed rows, left no breathing room between
				// "the read-state update settles" and "start opening the modal"; deferring to a
				// microtask lets this click's own re-render commit first.
				Promise.resolve().then(() => props.onOpenTask());
			}}
			onStop={onStop}
			agentPicker={props.agentPicker}
		/>
	);
};
